import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";

function initialsOf(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] || "") + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase() || "?";
}

function parsePct(v) {
  const n = parseFloat(String(v ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : NaN;
}

export default function CommercialCommissionSettings() {
  const [people, setPeople] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [saving, setSaving] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPct, setNewPct] = useState("");
  const [backfilling, setBackfilling] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/commercial-portal/commission-settings");
      const list = r?.data?.people || [];
      setPeople(list);
      const d = {};
      list.forEach((p) => {
        d[p.email] = p.setup_percent == null ? "" : String(p.setup_percent).replace(".", ",");
      });
      setDrafts(d);
    } catch (e) {
      setError(e?.response?.status === 403
        ? "Apenas o usuário master pode configurar comissões."
        : "Não foi possível carregar a equipe.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (email, value) => {
    const pct = parsePct(value);
    if (Number.isNaN(pct) || pct < 0 || pct > 100) {
      setError("Informe um percentual entre 0 e 100.");
      return false;
    }
    setSaving(email);
    setError("");
    setNotice("");
    try {
      await api.put("/commercial-portal/commission-settings", { email, setup_percent: pct });
      setNotice(`Comissão de ${email} salva: ${String(pct).replace(".", ",")}% da adesão.`);
      await load();
      return true;
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Não foi possível salvar.");
      return false;
    } finally {
      setSaving("");
    }
  };

  const addByEmail = async () => {
    const ok = await save(newEmail.trim().toLowerCase(), newPct);
    if (ok) { setNewEmail(""); setNewPct(""); }
  };

  const backfill = async () => {
    if (!window.confirm("Gerar comissões para contratos já assinados que ainda não têm comissão automática?")) return;
    setBackfilling(true);
    setError("");
    setNotice("");
    try {
      const r = await api.post("/commercial-portal/commission-settings/backfill", {});
      const c = r?.data?.created || 0;
      const m = r?.data?.missing || 0;
      setNotice(`${c} comissão(ões) gerada(s).` + (m ? ` ${m} pessoa(s) sem % cadastrado ficaram de fora.` : ""));
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Não foi possível gerar as comissões.");
    } finally {
      setBackfilling(false);
    }
  };

  const input = "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-[15px] text-slate-800 outline-none transition focus:border-[#5b3bf0] focus:ring-2 focus:ring-[#5b3bf0]/15";

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1b1464] via-[#3423b8] to-[#5b3bf0] px-4 py-8 sm:px-8">
      <div className="mx-auto max-w-5xl">
        <div className="rounded-[28px] bg-white px-6 py-6 shadow-xl sm:px-8">
          <a href="/commercial-portal" className="text-[13px] font-bold text-[#5b3bf0] hover:underline">← Voltar ao Portal Comercial</a>
          <div className="mt-3 text-[12px] font-bold uppercase tracking-[0.18em] text-[#5b3bf0]">Equipe</div>
          <h1 className="mt-1 text-[30px] font-extrabold text-[#1b1464]">Comissões da equipe</h1>
          <p className="mt-2 max-w-3xl text-[15px] text-slate-500">
            Defina o percentual sobre a <b>adesão</b> de cada consultor ou agendador. Quando um contrato é assinado,
            a comissão é criada automaticamente como <b>Prevista</b>. Mudanças valem para contratos assinados depois da alteração.
          </p>
        </div>

        {(error || notice) && (
          <div className={`mt-4 rounded-2xl px-5 py-3 text-[14px] font-semibold ${error ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`}>
            {error || notice}
          </div>
        )}

        <div className="mt-5 overflow-hidden rounded-[28px] bg-white shadow-xl">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-6 py-5 sm:px-8">
            <div>
              <div className="text-[17px] font-bold text-[#1b1464]">Percentual por pessoa</div>
              <div className="text-[13px] text-slate-500">Deixe em branco para não gerar comissão automática.</div>
            </div>
            <button type="button" onClick={backfill} disabled={backfilling}
              className="rounded-xl border border-[#5b3bf0]/25 bg-white px-4 py-2.5 text-[13px] font-bold text-[#5b3bf0] transition hover:bg-[#f4f1ff] disabled:opacity-50">
              {backfilling ? "Gerando..." : "Gerar para contratos já assinados"}
            </button>
          </div>

          {loading ? (
            <div className="px-8 py-10 text-center text-[15px] text-slate-400">Carregando equipe...</div>
          ) : people.length === 0 ? (
            <div className="px-8 py-10 text-center text-[15px] text-slate-400">Nenhuma pessoa na equipe ainda.</div>
          ) : (
            people.map((p) => {
              const missing = p.setup_percent == null;
              return (
                <div key={p.email} className="flex flex-wrap items-center gap-4 border-b border-slate-100 px-6 py-4 last:border-b-0 sm:px-8">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#ede9ff] text-[14px] font-extrabold text-[#5b3bf0]">
                    {initialsOf(p.name)}
                  </div>
                  <div className="min-w-[200px] flex-1">
                    <div className="text-[15px] font-bold text-slate-800">{p.name}</div>
                    <div className="text-[13px] text-slate-500">{p.email}{p.role ? ` · ${p.role}` : ""}</div>
                    {missing && <div className="mt-1 text-[12px] font-semibold text-amber-600">Sem percentual cadastrado</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="relative w-28">
                      <input className={input} inputMode="decimal" placeholder="0"
                        value={drafts[p.email] ?? ""}
                        onChange={(e) => setDrafts((d) => ({ ...d, [p.email]: e.target.value }))} />
                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[14px] font-bold text-slate-400">%</span>
                    </div>
                    <button type="button" disabled={saving === p.email}
                      onClick={() => save(p.email, drafts[p.email])}
                      className="rounded-xl bg-[#1b1464] px-4 py-2.5 text-[13px] font-bold text-white transition hover:bg-[#2a1f8f] disabled:opacity-50">
                      {saving === p.email ? "Salvando..." : "Salvar"}
                    </button>
                  </div>
                </div>
              );
            })
          )}

          <div className="flex flex-wrap items-end gap-3 bg-slate-50 px-6 py-5 sm:px-8">
            <div className="min-w-[240px] flex-1">
              <div className="mb-1 text-[12px] font-bold uppercase tracking-[0.12em] text-slate-500">Convidado ainda sem acesso (e-mail)</div>
              <input className={input} placeholder="consultor@simplersolucoes.com.br" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
            </div>
            <div className="w-28">
              <div className="mb-1 text-[12px] font-bold uppercase tracking-[0.12em] text-slate-500">% adesão</div>
              <input className={input} inputMode="decimal" placeholder="0" value={newPct} onChange={(e) => setNewPct(e.target.value)} />
            </div>
            <button type="button" onClick={addByEmail} disabled={!newEmail.includes("@") || saving}
              className="rounded-xl bg-[#5b3bf0] px-5 py-2.5 text-[13px] font-bold text-white transition hover:bg-[#4a2ee0] disabled:opacity-50">
              Adicionar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
